import { useState } from 'react';
import { ChevronDown, ExternalLink, ScrollText, Sparkles } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { Button } from '@/shared/ui/button';
import { Card, CardContent } from '@/shared/ui/card';
import type { PublicInvitePassbook } from '@/features/public-invite/api/use-public-invite';
import { PublicInviteRichText } from '@/features/public-invite/ui/public-invite-rich-text';

function SectionTitle({ icon: Icon, children }: { icon: typeof ScrollText; children: string }) {
  return (
    <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      <Icon className="size-3.5" />
      {children}
    </h4>
  );
}

/**
 * Regras de fidelidade (campos fixos do passbook, os mesmos do verso do cartão)
 * e termos/privacidade (`terms_and_conditions` em texto ou `link_terms_conditions`
 * externo — no legado são mutuamente exclusivos).
 */
export function RulesAndTerms({ passbook }: { passbook: PublicInvitePassbook }) {
  const [expanded, setExpanded] = useState(false);
  const terms = passbook.termsAndConditions?.trim();

  return (
    <Card className="mx-auto w-full max-w-xl">
      <CardContent className="space-y-7 pt-5">
        {passbook.loyaltyRules.length > 0 ? (
          <section className="space-y-4">
            <SectionTitle icon={Sparkles}>Regras de fidelidade</SectionTitle>
            {passbook.loyaltyRules.map((rule) => (
              <div key={rule.label}>
                <h3 className="text-sm font-semibold">{rule.label}</h3>
                <PublicInviteRichText html={rule.value} />
              </div>
            ))}
          </section>
        ) : null}

        {passbook.linkTermsConditions ? (
          <section className="space-y-3">
            <SectionTitle icon={ScrollText}>Termos e privacidade</SectionTitle>
            <p className="text-sm text-muted-foreground">
              Regulamento do programa e como seus dados são usados.
            </p>
            <Button asChild variant="outline">
              <a href={passbook.linkTermsConditions} target="_blank" rel="noopener noreferrer">
                Ler termos e condições
                <ExternalLink className="size-4" />
              </a>
            </Button>
          </section>
        ) : terms ? (
          <section className="space-y-3">
            <SectionTitle icon={ScrollText}>Termos e privacidade</SectionTitle>
            <div className="relative">
              <div className={cn(!expanded && 'max-h-56 overflow-hidden')} id="participant-terms">
                <PublicInviteRichText html={terms} />
              </div>
              {!expanded ? (
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[hsl(var(--glass-bg))] to-transparent" />
              ) : null}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded((v) => !v)}
              aria-expanded={expanded}
              aria-controls="participant-terms"
            >
              {expanded ? 'Mostrar menos' : 'Ler termos completos'}
              <ChevronDown className={cn('size-4 transition-transform', expanded && 'rotate-180')} />
            </Button>
          </section>
        ) : null}
      </CardContent>
    </Card>
  );
}
